import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import MbCoTrader from "./MbCoTrader";
import CoTraderStocksMobile from "./CoTraderStocksMobile";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import CoTraderCombinedMobileFiltersBar from "./CoTraderCombinedMobileFiltersBar";
import CoTraderDemoTopBar from "./CoTraderDemoTopBar";
import {
  DEFAULT_FX_TRADE_TYPE,
  EMPTY_TICKER_UNIVERSE,
  FX_ASSET_TYPE,
  FX_TRADE_TYPE_OPTIONS,
  fetchTickerUniverse,
  getFxRuntimeSymbol,
  resolveTickerUniverseSelection,
} from "./TickerUniverseOptions";
import {
  COTRADER_LANGUAGE_LIST,
  getStoredCoTraderLanguage,
} from "./Utilities/languages";

const DEMO_SELECTION_STORAGE_KEY = "cotrader_demo_combined_selection";
const DEMO_FX_TRADE_TYPE_OPTIONS = [FX_TRADE_TYPE_OPTIONS[0]];

const getStoredSelection = () => {
  try {
    const storedSelection = JSON.parse(
      localStorage.getItem(DEMO_SELECTION_STORAGE_KEY) || "null",
    );

    if (!storedSelection) {
      return null;
    }

    return {
      ...storedSelection,
      tradeType: DEFAULT_FX_TRADE_TYPE,
    };
  } catch (error) {
    console.warn("Failed to load CoTrader demo selection", error);
    return null;
  }
};

const CoTraderDemoMobile = () => {
  const { isDark } = useTheme();
  const [tickerUniverse, setTickerUniverse] = useState(EMPTY_TICKER_UNIVERSE);
  const [isUniverseLoading, setIsUniverseLoading] = useState(true);
  const [universeError, setUniverseError] = useState("");
  const [selection, setSelection] = useState(
    () =>
      getStoredSelection() || {
        assetType: "",
        country: null,
        category: null,
        tradeType: DEFAULT_FX_TRADE_TYPE,
        value: "",
      },
  );
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [language, setLanguage] = useState(() => getStoredCoTraderLanguage());
  const [languageKey, setLanguageKey] = useState(0);

  const styles = useMemo(
    () =>
      isDark
        ? {
            pageBg: "#0B0F1A",
            surface: "#12172A",
            border: "#1E2642",
            muted: "#A6B0CF",
          }
        : {
            pageBg: "#FFFFFF",
            surface: "#FFFFFF",
            border: "#E9ECEF",
            muted: "#5B6472",
          },
    [isDark],
  );

  useEffect(() => {
    let isMounted = true;

    const loadTickerUniverse = async () => {
      setIsUniverseLoading(true);
      setUniverseError("");

      try {
        const nextTickerUniverse = await fetchTickerUniverse({ isDemoMode: true });
        if (!isMounted) return;
        setTickerUniverse(nextTickerUniverse);
      } catch (error) {
        if (!isMounted) return;
        setTickerUniverse(EMPTY_TICKER_UNIVERSE);
        setUniverseError(error?.message || "Ticker universe is unavailable.");
      } finally {
        if (isMounted) {
          setIsUniverseLoading(false);
        }
      }
    };

    void loadTickerUniverse();

    return () => {
      isMounted = false;
    };
  }, []);

  const resolvedSelection = useMemo(
    () =>
      resolveTickerUniverseSelection({
        options: tickerUniverse.options,
        defaultSelection: tickerUniverse.defaultSelection,
        selection,
      }),
    [selection, tickerUniverse.defaultSelection, tickerUniverse.options],
  );
  const isFxAsset = resolvedSelection.assetType === FX_ASSET_TYPE;
  const scopedStockOptions = useMemo(() => {
    if (isFxAsset || !resolvedSelection.assetType) {
      return {};
    }

    return tickerUniverse.options[resolvedSelection.assetType] || {};
  }, [isFxAsset, resolvedSelection.assetType, tickerUniverse.options]);

  useEffect(() => {
    localStorage.setItem(
      DEMO_SELECTION_STORAGE_KEY,
      JSON.stringify({
        assetType: resolvedSelection.assetType,
        country: resolvedSelection.country,
        category: resolvedSelection.category,
        tradeType: resolvedSelection.tradeType,
        value: resolvedSelection.value,
      }),
    );

    if (isFxAsset) {
      localStorage.setItem("default_symbol", getFxRuntimeSymbol(resolvedSelection.value));
      localStorage.setItem(
        "cotrader_combined_trade_type",
        resolvedSelection.tradeType || DEFAULT_FX_TRADE_TYPE,
      );
      return;
    }

    if (resolvedSelection.country) {
      localStorage.setItem("default_country_stock", resolvedSelection.country);
    }
    if (resolvedSelection.category) {
      localStorage.setItem("default_category_stock", resolvedSelection.category);
    }
    if (resolvedSelection.value) {
      localStorage.setItem("default_symbol_stock", resolvedSelection.value);
    }
  }, [isFxAsset, resolvedSelection]);

  useEffect(() => {
    if (localStorage.getItem("user_language") !== language) {
      localStorage.setItem("user_language", language);
    }
  }, [language]);

  const renderEmbeddedProduct = () => {
    if (!resolvedSelection.assetType || !resolvedSelection.option) {
      return (
        <div className="px-3 pb-3">
          <div
            className="rounded-4 border px-3 py-3"
            style={{
              background: styles.surface,
              borderColor: styles.border,
              color: styles.muted,
              fontWeight: 600,
            }}
          >
            {universeError || "No eligible CoTrader instruments are available for this user."}
          </div>
        </div>
      );
    }

    if (isFxAsset) {
      return (
        <MbCoTrader
          hideTopChrome
          isDemoMode
          instrumentTypeOverride={resolvedSelection.tradeType}
          languageOverride={language}
          selectedSymbolOverride={getFxRuntimeSymbol(resolvedSelection.value)}
          showSuggestionsOverride={showSuggestions}
        />
      );
    }

    return (
      <CoTraderStocksMobile
        defaultSymbolOverride={resolvedSelection.value}
        hideTopChrome
        isDemoMode
        languageOverride={language}
        selectedSymbolOverride={resolvedSelection.value}
        selectedTickerLabelOverride={resolvedSelection.option.label || ""}
        showSuggestionsOverride={showSuggestions}
        stockOptionsOverride={scopedStockOptions}
        stockUniverseErrorOverride={universeError}
        stockUniverseLoadingOverride={isUniverseLoading}
      />
    );
  };

  return (
    <>
      <Helmet>
        <title>CoTrader | EMAlpha</title>
      </Helmet>

      <div
        className="w-100 h-100 d-flex flex-column"
        style={{ height: "100dvh", background: styles.pageBg }}
        key={languageKey}
      >
        <CoTraderDemoTopBar isDark={isDark} />

        <CoTraderCombinedMobileFiltersBar
          universeOptions={tickerUniverse.options}
          selection={resolvedSelection}
          onSelectionChange={setSelection}
          isDark={isDark}
          isDisabled={isUniverseLoading || Boolean(universeError)}
          tradeTypeOptions={DEMO_FX_TRADE_TYPE_OPTIONS}
          showHeaderSummary={false}
          showSuggestions={showSuggestions}
          setShowSuggestions={setShowSuggestions}
          language={language}
          setLanguage={setLanguage}
          languageList={COTRADER_LANGUAGE_LIST}
          setLanguageKey={setLanguageKey}
        />

        <div className="flex-grow-1 overflow-hidden">{renderEmbeddedProduct()}</div>
      </div>
    </>
  );
};

export default CoTraderDemoMobile;
